// @erp/module-fin/schemas — the FIN grant matrix. Browser-safe: types + plain
// data only (fe/useCan and the gateway's rbac() evaluate the SAME map through
// can(), so UI affordance and server enforcement can never disagree).
//
// Capability families:
//   fin:read   — lists/detail (invoices, ledger, accounts read, console entry)
//   fin:manage — accounts config, create/void invoices, credit notes (later)
//   fin:verify — approve/reject claims, settlement retry (the checker tier)
//   fin:cash   — receive money in PERSON: cash, and a demand draft in hand. The
//                most sensitive grant in the product, because an entry made
//                under it is born `paid` with no queue and no second actor: the
//                person holding the instrument IS the check. Its real control is
//                the physical receipt book, not the system, which is why it sits
//                on exactly one role and never rides fin:* by convenience.
//   fin:pay    — the payer surface (my invoices, submit claim, my uploads)
//
// Role mapping mirrors LIB's style: the module-agnostic operator tier
// (owner/admin — the roles that get lib:* there) gets fin:*; the payer-tier
// member roles (student, patron, candidate, parent) get fin:pay. platform_admin
// stays core-only (the LIB/CMS precedent — module grants never ride the
// platform role).
//
// AMENDED 2026-09-07 (ADR-008 §2): an `accountant` role now EXISTS and is the
// only holder of fin:cash. The sentence this replaced said no dedicated
// finance-operator mapping is minted, and it sat directly above the map that
// mints one — stale by a whole ADR, and the kind of comment that makes a reader
// working top-down doubt the code instead of the prose.
//
// THE MODULE-SCOPED VERIFIER RULE (user requirement 2026-08-21) is deliberately
// NOT a grant: verification of a payment whose invoice.module = m is allowed to
// holders of fin:verify OR m:desk OR m:admin — an OR-tier no single rbac(perm)
// can express, enforced in-service via the injected can() (allowedToVerify in
// api/service.ts). A lib:desk assistant therefore verifies LIBRARY invoices
// without holding any fin:* capability, and the queue auto-scopes to their
// modules.
//
// Merge law: compose with the core seed via mergeGrants(ROLE_GRANTS, FIN_GRANTS)
// — NEVER an object spread (the LIB anti-clobber law).
import type { RoleGrants } from "@erp/auth-kit/rbac";

export const FIN_GRANTS: RoleGrants = {
  owner: ["fin:*"],
  admin: ["fin:*"],
  // THE finance operator. Holds the money duties and NOT fin:manage — voiding an
  // invoice and reconfiguring receiving accounts are a different power from
  // receiving and checking money, and the two are kept apart on purpose. Grant
  // fin:manage explicitly if the institution wants one person to hold both.
  // fin:payout is ADR-008 §4's outbound capability, and it is a NEW grant rather
  // than a reuse for reasons that cut both ways: fin:manage is deliberately
  // withheld from this role (receiving money and voiding an invoice are
  // different powers), and fin:cash is inbound by definition — custody of money
  // the institution HOLDS says nothing about sending it.
  accountant: ["fin:read", "fin:verify", "fin:cash", "fin:payout"],
  student: ["fin:pay"],
  patron: ["fin:pay"],
  candidate: ["fin:pay"],
  parent: ["fin:pay"],
};

/**
 * Every capability that admits a session to the /finance console (any one
 * suffices — the CMS_CONSOLE_PERMS precedent). fin:pay is deliberately absent:
 * payers use the member portals, never the admin console. The lib-rule
 * verifiers (m:desk/m:admin without fin:*) use the STAFF portal's payments
 * desk, not the admin console — so no lib perms appear here either.
 */
export const FIN_CONSOLE_PERMS = ["fin:read", "fin:manage", "fin:verify"] as const;
